import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import Chip from '@mui/material/Chip';
import { Translate, openFile, byteSize, TextFormat } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { APP_LOCAL_DATE_FORMAT } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntity } from './place.reducer';

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
    {children}
  </Typography>
);

const FieldValue = ({ children, sx }: { children: React.ReactNode; sx?: object }) => (
  <Typography variant="body1" sx={{ mb: 2, ...sx }}>
    {children || '-'}
  </Typography>
);

const BooleanDisplay = ({ value }: { value?: boolean }) =>
  value ? (
    <Chip icon={<FontAwesomeIcon icon="check" />} label="Yes" color="success" size="small" />
  ) : (
    <Chip icon={<FontAwesomeIcon icon="times" />} label="No" color="error" size="small" />
  );

export const PlaceDetail = () => {
  const dispatch = useAppDispatch();

  const { id } = useParams<'id'>();

  useEffect(() => {
    dispatch(getEntity(id));
  }, []);

  const placeEntity = useAppSelector(state => state.place.entity);
  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <h2 data-cy="placeDetailsHeading">
          <Translate contentKey="jhipsterSampleApplicationApp.place.detail.title">Place</Translate>
        </h2>
        {placeEntity.id && (
          <Box sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', px: 2, py: 0.5, borderRadius: 1, fontWeight: 'bold' }}>
            ID: {placeEntity.id}
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.code" />
          </FieldLabel>
          <FieldValue>{placeEntity.code}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.enabled" />
          </FieldLabel>
          <BooleanDisplay value={placeEntity.enabled} />

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.icon" />
          </FieldLabel>
          <FieldValue>{placeEntity.icon}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.destinationSight" />
          </FieldLabel>
          <BooleanDisplay value={placeEntity.destinationSight} />

          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.place.longitude" />
              </FieldLabel>
              <FieldValue>{placeEntity.longitude}</FieldValue>
            </Grid>
            <Grid size={{ xs: 6 }}>
              <FieldLabel>
                <Translate contentKey="jhipsterSampleApplicationApp.place.latitude" />
              </FieldLabel>
              <FieldValue>{placeEntity.latitude}</FieldValue>
            </Grid>
          </Grid>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.createdDate" />
          </FieldLabel>
          <FieldValue>
            {placeEntity.createdDate ? <TextFormat value={placeEntity.createdDate} type="date" format={APP_LOCAL_DATE_FORMAT} /> : null}
          </FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.defaultImage" />
          </FieldLabel>
          <FieldValue>{placeEntity.defaultImage}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.defaultImageData" />
          </FieldLabel>
          {placeEntity.defaultImageData ? (
            <Box sx={{ mb: 2 }}>
              {placeEntity.defaultImageDataContentType ? (
                <a onClick={openFile(placeEntity.defaultImageDataContentType, placeEntity.defaultImageData)}>
                  <img
                    src={`data:${placeEntity.defaultImageDataContentType};base64,${placeEntity.defaultImageData}`}
                    style={{ maxHeight: '30px' }}
                  />
                </a>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {placeEntity.defaultImageDataContentType}, {byteSize(placeEntity.defaultImageData)}
              </Typography>
            </Box>
          ) : (
            <FieldValue>No image</FieldValue>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.createdBy" />
          </FieldLabel>
          <FieldValue>{placeEntity.createdBy ? placeEntity.createdBy.login : ''}</FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.tags" />
          </FieldLabel>
          <FieldValue>
            {placeEntity.tags
              ? placeEntity.tags.map((val, i) => (
                  <span key={val.id}>
                    <a>{val.code}</a>
                    {placeEntity.tags && i === placeEntity.tags.length - 1 ? '' : ', '}
                  </span>
                ))
              : null}
          </FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.category" />
          </FieldLabel>
          <FieldValue>
            {placeEntity.categories
              ? placeEntity.categories.map((val, i) => (
                  <span key={val.id}>
                    <a>{val.code}</a>
                    {placeEntity.categories && i === placeEntity.categories.length - 1 ? '' : ', '}
                  </span>
                ))
              : null}
          </FieldValue>

          <FieldLabel>
            <Translate contentKey="jhipsterSampleApplicationApp.place.destination" />
          </FieldLabel>
          <FieldValue>{placeEntity.destination ? placeEntity.destination.code : ''}</FieldValue>
        </Grid>

        <Grid size={12}>
          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button component={Link} to="/place" replace color="info" variant="outlined" startIcon={<FontAwesomeIcon icon="arrow-left" />}>
              <Translate contentKey="entity.action.back">Back</Translate>
            </Button>
            <Button
              component={Link}
              to={`/place/${placeEntity.id}/edit`}
              replace
              color="primary"
              variant="contained"
              startIcon={<FontAwesomeIcon icon="pencil-alt" />}
            >
              <Translate contentKey="entity.action.edit">Edit</Translate>
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlaceDetail;
